import {
    FrameMetadata,
    IllustrationMetadata,
    ModuleMetadata,
    RegionElement,
    TextMetadata,
    StepSelections,
    Transaction,
    CommandWithFollowup,
    Product,
    Customer,
    SilentStepData,
    Placeable,
    StepStorage,
    VariantResource,
    ILayout,
    LayoutState,
    SerializableStep,
    Workflow,
    LayoutsState,
    StepType,
    ModelStepData,
    MaterialStepData,
    SerializableWorkflow,
    IllustrationElement,
    FrameElement,
    Step,
    AnyStepData,
    DigitalContentStepData,
    FrameStepData,
    IllustrationStepData,
    ModuleStepData,
    PictureStepData,
    QuestionStepData,
    ShapeStepData,
    TextStepData,
} from "../types";
import {
    getAllScenes,
    getActiveScenes,
    getRenderableRelevantScenes,
    stepConditionsAreSatisfied,
    WorkflowScene,
} from "./scenes";
import { PromiseQueue, QueueablePromise } from "../util/PromiseQueue";
import { FetchResult, MutationOptions } from "@apollo/client/core";
import { FrameService } from "../services/steps/frame/Service";
import { Poller } from "../util/Poller";
import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";
import { SilentStepHandler, SilentStepTriggerResult } from "../services/SilentStepHandler";
import { digitalContentStepService } from "../services/steps/digitalContent";
import { frameStepService } from "../services/steps/frame";
import { illustrationStepService } from "../services/steps/illustration";
import { materialStepService } from "../services/steps/material";
import { modelStepService } from "../services/steps/model";
import { moduleStepService } from "../services/steps/module";
import { pictureStepService } from "../services/steps/picture";
import { questionStepService } from "../services/steps/question";
import { shapeStepService } from "../services/steps/shape";
import { textStepService } from "../services/steps/text";
import { ModuleProduct } from "../module/ModuleProduct";
import moduleResolver from "../module/resolver";
import { UnhandledBehaviorError } from "../util/exception";
import { LayoutPreviewService } from "./LayoutPreviewService";
import type { ModelContainer, ThreeDPreviewService } from "@spiffcommerce/preview";
import { SpiffCommerceClient } from "../client";
import { graphQlManager } from "../services/server";
import {
    getConversionConfigurationQuery,
    getTransactionStakeholdersQuery,
    updateTransactionWorkflowQuery,
} from "../query";
import { WorkflowExperience } from "../WorkflowExperience";
import { CanvasCommand, DeleteElementCommand, GroupCommand, UpdateWorkflowStateCommand } from "../command";
import { CommandContext, CommandState } from "../CommandContext";
import { generate } from "../util/guid";
import {
    createRecipientQuery,
    transactionApprovalQuery,
    transactionAttachRecipientQuery,
    transactionRejectionQuery,
    updateRecipientQuery,
} from "../Bundle/query";
import {
    ConversionConfiguration,
    ConversionDataType,
    FrameStepHandle,
    Recipient,
    WorkflowExperienceEventType,
    WorkflowPanel,
} from "..";
import { GraphQlClientFunc } from "../services/graphql";

export interface SilentIllustrationStepData extends SilentStepData, Placeable {}

export interface ProductOverlayStepData extends SilentStepData, Placeable {}

export interface EditedSteps {
    [stepName: string]: boolean;
}

export interface MandatorySteps {
    [stepName: string]: boolean;
}

export type StepElements = {
    [key: string]: RegionElement[];
};

export type StepInitialised = {
    [key: string]: boolean;
};

export type StepMetadata = FrameMetadata | IllustrationMetadata | ModuleMetadata | TextMetadata;

// Non-variant decisions made in each step.
export interface WorkflowStorage {
    [stepName: string]: StepStorage;
}

export interface WorkflowMetadata {
    [stepName: string]: StepMetadata;
}

export interface WorkflowSelections {
    [stepName: string]: {
        selections: VariantResource[];
    };
}

export interface InformationResult {
    message: string;
    messageType: InformationMessageType;
    stepID: string;
}

export enum InformationMessageType {
    Error = "Error",
    Warning = "Warning",
    Info = "Info",
}

/**
 * Services required for the operation of individual steps.
 */
export interface StepSpecificServices {
    frameService?: FrameService;
    module?: ModuleProduct;
}

export interface ValidationErrors {
    steps: Map<string, Map<string, string>>;
}

export type DesignCreationProgressUpdate = (message: string) => void;
export type ConfirmCallback = (isConfirmed: boolean) => void;
export type EditedCallback = (editedSteps: EditedSteps) => void;
export type ElementsCallback = (elements: StepElements) => void;
export type EnsuredInitialisedCallback = (ensuredInitialised: boolean) => void;
export type InformationResultCallback = (messages: InformationResult[]) => void;
export type InitCallback = (stepInitialised: StepInitialised) => void;
export type MakingAdjustmentsCallback = (makingAdjustments: string) => void;
export type MandatoryCallback = (mandatorySteps: MandatorySteps) => void;
export type MetadataCallback = (metadata: WorkflowMetadata) => void;
export type SelectionCallback = (callbackOptions: {
    selectionCost: number;
    selections: StepSelections;
    traversableScenes: WorkflowScene[];
}) => void;
export type StepSpecificStorageCallback = (selections: StepStorage) => void;
export type StorageCallback = (storage: WorkflowStorage) => void;
export type ValidationCallback = (validationErrors: ValidationErrors) => void;
export type RecipientCallback = (recipient: Recipient | undefined) => void;

export type StateMutationFunc = (
    options?: Omit<MutationOptions, "mutation">,
) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;

export type GraphQlRequest = (
    options?: Omit<MutationOptions, "mutation">,
) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;

export const STATE_DEBOUNCE_TIME = 1000;

/**
 * Encapsulates the behavior of updating layout state against a transaction on the server.
 */
export class WorkflowStatePromise extends QueueablePromise {
    private readonly update: () => Promise<any>;
    public readonly createdAt: Date = new Date();

    constructor(update: () => Promise<any>) {
        super();
        this.update = update;
    }
    public async execute() {
        await this.update();
        await new Promise<void>((resolve) => {
            window.setTimeout(() => {
                resolve();
            }, STATE_DEBOUNCE_TIME);
        });
    }
}

export interface WorkflowManager {
    addPoller: (poller: Poller) => void;
    addConfirmCallback: (callback: ConfirmCallback) => void;
    addEditedCallback: (callback: EditedCallback) => void;
    addElementsCallback: (callback: ElementsCallback) => void;
    addInformationResultCallback: (callback: InformationResultCallback) => void;
    addInitCallback: (callback: InitCallback) => void;
    addMakingAdjustmentsCallback: (callback: MakingAdjustmentsCallback) => void;
    addMandatoryCallback: (callback: MandatoryCallback) => void;
    addMetadataCallback: (callback: MetadataCallback) => void;
    addSelectionCallback: (callback: SelectionCallback) => void;
    addStepSpecificStorageCallback: (callback: StepSpecificStorageCallback, stepName: string) => void;
    addStorageCallback: (callback: StorageCallback) => void;
    addValidationCallback: (callback: ValidationCallback) => void;
    addRecipientCallback: (callback: RecipientCallback) => void;
    removeRecipientCallback: (callback: RecipientCallback) => void;
    getCommandDispatcher: () => (command: CanvasCommand) => void;
    getCommandContext: () => CommandContext;
    getLayouts: () => ILayout[];
    getAllLayoutData: () => LayoutState[];
    getLayoutPreviewService: () => LayoutPreviewService | undefined;
    getPreviewService: () => ThreeDPreviewService | undefined;
    getRegionElements: (stepName: string) => RegionElement[];
    getSerializedStep: (stepName: string, serializedSteps: SerializableStep[]) => SerializableStep | undefined;
    getStepSpecificServices: (stepName: string) => StepSpecificServices | undefined;
    getStepStorage: (stepName: string) => StepStorage | undefined;
    getMetadata: (stepName: string) => StepMetadata | undefined;
    getWorkflowMetadata: () => WorkflowMetadata;
    getInformationResults(): InformationResult[];
    getTransactionCustomer: () => Customer | undefined;
    setTransactionCustomer: (customer: Customer) => void;

    /**
     * Sets the current workflow for this experience.
     * @param workflow The new workflow to set.
     * @returns A promise that resolves when the workflow has been set.
     */
    setWorkflow: (workflow: Workflow) => Promise<void>;

    /**
     * @deprecated Use setTransactionCustomer instead.
     */
    setTransactionCustomerDetails: (details: { email: string }) => void;
    getWorkflowSelections: () => WorkflowSelections;
    getStepSelections: () => StepSelections;
    /**
     * A promise resolving when the initial state of the workflow has completed loading.
     */
    getInitializationPromise: () => Promise<void>;
    /**
     * Returns true when the initial state of the workflow has been loaded and settled.
     */
    isInitialized(): boolean;
    markStepsAsInitialised: (stepNames: string[]) => void;
    getUpdatesPending: () => boolean;
    markUpdateCompleted: (id: string) => void;
    markUpdatePending: () => string;
    outstandingRequestsPromise: () => Promise<void>;
    reset: () => Promise<void>;
    setCurrentAdjustingStepId: (stepId: string) => void;
    setEditedStatus: (stepName: string, status: boolean) => void;
    setInformationResults: (results: InformationResult[]) => void;
    setMandatoryFulfilled: (stepName: string, status: boolean) => void;
    getMandatoryUnfulfilledSteps: () => string[];
    setSelectionsAndElements: (
        stepName: string,
        variants: VariantResource[],
        elements: RegionElement[],
        callback?: () => Promise<void>,
    ) => Promise<void>;
    getStateHash(): Promise<string>;
    setStepError: (stepName: string, field: string, error: string | undefined) => void;
    /** Gets any validation errors for a specific step, or `undefined` if there are none. */
    getStepErrors: (stepName: string) => Map<string, string> | undefined;
    /** Gets validation errors for all steps. Only steps with errors will be present. */
    getValidationErrors: () => ValidationErrors;
    toggleDesignConfirmed: () => void;
    updateMetadata: (stepName: string, update: any) => void;
    /**
     * @deprecated State updates are being pulled internally. This function will be removed in the future.
     */
    updateStateWithServerImmediate: (getReducerState: () => CommandState) => Promise<void>;
    updateStorage: (stepName: string, update: StepStorage) => void;
    getModelContainer: () => ModelContainer | undefined;
    setModelContainer: (container: ModelContainer) => void;

    /**
     * Injects the model of this product into the preview service.
     * This also overwrites the internal preview service.
     * @param previewService The preview service to inject into.
     * @param refocusCamera Optional: Run camera focus Defaults to `true`.
     */
    injectIntoPreviewService(previewService: ThreeDPreviewService, refocusCamera?: boolean): Promise<void>;

    /**
     * Ejects the model of this product from the preview service. This also sets the internal preview service to undefined.
     */
    ejectFromPreviewService(): void;

    /**
     * Enables or disables uploading of workflow state to the server.
     * @param enabled When true, workflow state will be uploaded to the server.
     */
    setWorkflowStateSyncEnabled: (enabled: boolean) => void;

    /**
     * Returns the client that was responsible for spawning this manager.
     */
    getClient(): SpiffCommerceClient;

    /**
     * Returns the workflow experience that was responsible for creating this manager.
     */
    getWorkflowExperience(): WorkflowExperience;

    /**
     * Re-fetches the transactionShareActions for this WorkflowManager's Transaction object.
     * The updated transactionShareActions overwrite the existing values on the Transaction object returned by `getTransaction()`.
     */
    updateTransactionShareActions(): Promise<void>;

    /**
     * Re-fetches the stakeholders and currentStakeholder for this WorkflowManager's Transaction object.
     * The updated stakeholders and currentStakeholder overwrite the existing values on the Transaction object returned by `getTransaction()`.
     */
    updateTransactionStakeholders(): Promise<void>;

    /**
     * Returns the tags for a step.
     */
    getStepTags(stepId: string): string[];

    approveTransaction(note?: string): Promise<void>;

    rejectTransaction(note?: string): Promise<void>;

    /**
     * Create or amend the recipient fo the transaction.
     */
    updateRecipient(
        firstName?: string,
        lastName?: string,
        address?: string,
        suburb?: string,
        state?: string,
        email?: string,
        postalCode?: string,
        country?: string,
        mobile?: string,
        company?: string,
        apartment?: string,
        customField1?: string,
        customField2?: string,
        customField3?: string,
        customField4?: string,
        customField5?: string,
        conversionConfigurationId?: string,
        prevalidated?: boolean,
    ): Promise<void>;

    /**
     * Returns the context object used for text templating.
     */
    getTemplatingContext(): { [key: string]: any };

    traversableScenes(): Promise<WorkflowScene[]>;
}

const createLayoutsFromPanels = (panels: WorkflowPanel[]): ILayout[] => {
    return panels
        .sort((a, b) => a.index - b.index)
        .map((panel: WorkflowPanel): ILayout => {
            return {
                id: generate(),
                panelId: panel.name,
                name: panel.name,
                title: panel.title,
                index: panel.index,
                createdAt: new Date(),
                updatedAt: new Date(),
                transparentBackground: panel.transparentBackground,
                height: panel.height,
                width: panel.width,
                previewRegion: panel.previewRegion,
                useEditableArea: panel.useEditableArea,
                editableArea: panel.editableArea,
            };
        });
};

const getInitialLayouts = (transaction: Transaction, workflow: Workflow) => {
    const serializedState = transaction.workflowState;
    const initialReducerState = serializedState ? (JSON.parse(serializedState) as LayoutsState) : undefined;
    if (initialReducerState) {
        return Object.values(initialReducerState.layouts).map((state) => state.layout);
    }
    return createLayoutsFromPanels(workflow.panels);
};

export class InternalWorkflowManager implements WorkflowManager {
    readonly client: SpiffCommerceClient;
    readonly experience: WorkflowExperience;

    private reloadedState?: LayoutsState;
    private ownerCustomer?: Customer;
    private updateTransaction: StateMutationFunc;
    private graphQlClient: GraphQlClientFunc;
    private confirmedDesign: boolean;
    private editedSteps: EditedSteps;
    private informationResults: InformationResult[];
    private layouts: ILayout[];
    private mandatorySteps: MandatorySteps;
    private pendingUpdates: string[];
    private selectionCost: number;
    private stepSpecificServices: { [stepName: string]: StepSpecificServices };
    private previewService: ThreeDPreviewService | undefined;
    private pollers: Poller[];
    private commandContext: CommandContext;
    private validationErrors: ValidationErrors;

    private stepElements: StepElements;
    private stepInitialised: StepInitialised;
    private stepMetadata: WorkflowMetadata;
    private stepSelections: StepSelections;
    private storage: WorkflowStorage;

    private confirmCallbacks: ConfirmCallback[];
    private editedCallbacks: EditedCallback[];
    private elementsCallbacks: ElementsCallback[];
    private informationResultCallbacks: InformationResultCallback[];
    private initCallbacks: InitCallback[];
    private makingAdjustmentsCallback: MakingAdjustmentsCallback[];
    private mandatoryCallbacks: MandatoryCallback[];
    private metadataCallbacks: MetadataCallback[];
    private selectionCallbacks: SelectionCallback[];
    private stepSpecificStorageCallbacks: {
        [stepName: string]: StepSpecificStorageCallback[];
    };
    private storageCallbacks: StorageCallback[];
    private validationCallbacks: ValidationCallback[];
    private recipientCallbacks: RecipientCallback[];

    private allScenes: WorkflowScene[];

    private currentAdjustingStepId: string;

    private renderableContextService?: LayoutPreviewService;
    private workflowStatePromiseQueue: PromiseQueue<WorkflowStatePromise> = new PromiseQueue<WorkflowStatePromise>(1);

    private initializationPromise: Promise<void>; // A promise that resolves when the initial state of the workflow has been loaded and settled.
    private initialized: boolean = false; // True when the initial state of the workflow has been loaded and settled.
    private readOnly: boolean = false;
    private modelContainer: ModelContainer | undefined;
    private isReloadedTransaction: boolean = false; // True when the transaction is being reloaded, rather than newly created.

    /** @deprecated This flag is made irrelevant by the introduction of step-specific silent flag */
    private singleVariantsRenderable: boolean = false;

    private stepTags: { [stepId: string]: string[] } = {};

    private recipientConversionConfiguration: ConversionConfiguration | undefined;

    constructor(
        experience: WorkflowExperience,
        client: SpiffCommerceClient,
        updateTransaction: StateMutationFunc,
        graphQlClient: GraphQlClientFunc,
        reloadedState?: LayoutsState,
        readOnly: boolean = false,
        modelContainer?: ModelContainer,
        /** @deprecated Should be determined via reloadedState */
        isReloadedTransaction: boolean = false,
        /** @deprecated Silent flag on steps makes this irrelevant */
        singleVariantsRenderable: boolean = false,
        /** @deprecated System should be smart enough to understand when to do this without manual instruction */
        delayWorkflowStateSync: boolean = false,
    ) {
        this.experience = experience;
        this.client = client;
        this.updateTransaction = updateTransaction;
        this.graphQlClient = graphQlClient;

        this.reloadedState = reloadedState;
        this.readOnly = readOnly;
        this.singleVariantsRenderable = singleVariantsRenderable;

        this.commandContext = new CommandContext();

        this.confirmCallbacks = [];
        this.editedCallbacks = [];
        this.elementsCallbacks = [];
        this.informationResultCallbacks = [];
        this.initCallbacks = [];
        this.mandatoryCallbacks = [];
        this.makingAdjustmentsCallback = [];
        this.metadataCallbacks = [];
        this.selectionCallbacks = [];
        this.stepSpecificStorageCallbacks = {};
        this.storageCallbacks = [];
        this.validationCallbacks = [];
        this.recipientCallbacks = [];

        this.modelContainer = modelContainer;

        this.confirmedDesign = false;
        this.editedSteps = {};
        this.informationResults = [];
        this.mandatorySteps = {};
        this.pendingUpdates = [];
        this.selectionCost = 0;
        this.stepSpecificServices = {};
        this.pollers = [];
        this.allScenes = [];

        this.stepElements = {};
        this.stepInitialised = {};
        this.stepMetadata = {};
        this.stepSelections = {};
        this.storage = {};
        this.validationErrors = { steps: new Map() };

        this.currentAdjustingStepId = "";

        this.isReloadedTransaction = isReloadedTransaction;

        if (delayWorkflowStateSync) {
            this.workflowStatePromiseQueue.enabled = false;
        }

        const workflow = this.experience.getWorkflow();
        if (workflow) {
            this.setWorkflow(workflow);
        }
    }

    /**
     * Sets the current workflow for this experience.
     * @param workflow The new workflow to set.
     * @returns A promise that resolves when the workflow has been set.
     */
    setWorkflow: (workflow: Workflow) => Promise<void> = async (workflow: Workflow) => {
        // Clear existing state
        this.confirmedDesign = false;
        this.editedSteps = {};
        this.informationResults = [];
        this.mandatorySteps = {};
        this.pendingUpdates = [];
        this.selectionCost = 0;
        this.stepSpecificServices = {};
        this.pollers = [];
        this.allScenes = [];
        this.stepElements = {};
        this.stepInitialised = {};
        this.stepMetadata = {};
        this.stepSelections = {};
        this.storage = {};
        this.validationErrors = { steps: new Map() };
        this.currentAdjustingStepId = "";

        // We either reload initial state or we create a new one.
        if (this.reloadedState) {
            // FIXME: What if reloadedState is for a different workflow?
            this.layouts = Object.values(this.reloadedState.layouts || {}).map((x) => x.layout);
        } else if (workflow) {
            this.layouts = getInitialLayouts(this.experience.getTransaction(), workflow);
        } else {
            this.layouts = [];
        }

        // Initialize the command context to a default state.
        this.commandContext.initialize(this.layouts, this.reloadedState);
        // Construct a renderable service for this manager.
        this.renderableContextService = new LayoutPreviewService(this.layouts);
        this.renderableContextService.setWorkflowManager(this);
        // Load step tags
        workflow.steps.forEach((step) => {
            this.stepTags[step.stepName] = step.tags ?? [];
        });

        const ps = this.previewService;
        this.ejectFromPreviewService();

        // Fire initialisation process.
        this.initializationPromise = this.initializeDefaultWorkflowState(workflow);
        this.initializationPromise.then(() => {
            this.initialized = true;
        });
        this.initializationPromise.catch((e) => {
            console.error(e);
            this.initialized = false;
            throw new Error("Workflow initialization failed due to an error. See console.");
        });
        this.initializationPromise.finally(() =>
            this.commandContext.registerStateCallback(() => {
                this.updateStateWithServer();
                this.renderLayouts();
            }),
        );

        if (ps) {
            await this.injectIntoPreviewService(ps, true);
        }
    };

    getTemplatingContext() {
        const recipient = this.experience.getTransaction().recipient;
        const c = { recipient };
        this.recipientConversionConfiguration?.requestedDataItems?.forEach((item) => {
            const id = item.uniqueIdentifier ?? item.title ?? "";
            if (item.type === ConversionDataType.FirstName) {
                c[id] = recipient?.firstName;
            } else if (item.type === ConversionDataType.LastName) {
                c[id] = recipient?.lastName;
            } else if (item.type === ConversionDataType.Custom) {
                if (item.customFieldIndex === 1) {
                    c[id] = recipient?.customField1;
                } else if (item.customFieldIndex === 2) {
                    c[id] = recipient?.customField2;
                } else if (item.customFieldIndex === 3) {
                    c[id] = recipient?.customField3;
                } else if (item.customFieldIndex === 4) {
                    c[id] = recipient?.customField4;
                } else if (item.customFieldIndex === 5) {
                    c[id] = recipient?.customField5;
                }
                if (item.option) {
                    c[id] = item.option.variants?.find((v) => v.id === c[id])?.name;
                }
            }
        });
        return c;
    }

    async updateRecipient(
        firstName?: string,
        lastName?: string,
        address?: string,
        suburb?: string,
        state?: string,
        email?: string,
        postalCode?: string,
        country?: string,
        mobile?: string,
        company?: string,
        apartment?: string,
        customField1?: string,
        customField2?: string,
        customField3?: string,
        customField4?: string,
        customField5?: string,
        conversionConfigurationId?: string,
        prevalidated?: boolean,
    ): Promise<void> {
        const transaction = this.experience.getTransaction();
        if (transaction.recipient?.id) {
            this.experience.getTransaction().recipient = {
                id: transaction.recipient.id,
                firstName: firstName ?? transaction.recipient.firstName,
                lastName: lastName ?? transaction.recipient.lastName,
                address: address ?? transaction.recipient.address,
                suburb: suburb ?? transaction.recipient.suburb,
                state: state ?? transaction.recipient.state,
                email: email ?? transaction.recipient.email,
                postalCode: postalCode ?? transaction.recipient.postalCode,
                country: country ?? transaction.recipient.country,
                mobile: mobile ?? transaction.recipient.mobile,
                company: company ?? transaction.recipient.company,
                apartment: apartment ?? transaction.recipient.apartment,
                customField1: customField1 ?? transaction.recipient.customField1,
                customField2: customField2 ?? transaction.recipient.customField2,
                customField3: customField3 ?? transaction.recipient.customField3,
                customField4: customField4 ?? transaction.recipient.customField4,
                customField5: customField5 ?? transaction.recipient.customField5,
                conversionConfigurationId: conversionConfigurationId || transaction.recipient.conversionConfigurationId,
            };
            this.experience.callEvent(WorkflowExperienceEventType.RecipientChanged);
            await this.graphQlClient().mutate({
                mutation: updateRecipientQuery,
                errorPolicy: "all",
                fetchPolicy: "no-cache",
                variables: {
                    id: transaction.recipient.id,
                    firstName: firstName || transaction.recipient.firstName,
                    lastName: lastName || transaction.recipient.lastName,
                    address: address || transaction.recipient.address,
                    suburb: suburb || transaction.recipient.suburb,
                    state: state || transaction.recipient.state,
                    email: email || transaction.recipient.email,
                    postalCode: postalCode || transaction.recipient.postalCode,
                    country: country || transaction.recipient.country,
                    mobile: mobile || transaction.recipient.mobile,
                    company: company || transaction.recipient.company,
                    apartment: apartment || transaction.recipient.apartment,
                    customField1: customField1 || transaction.recipient.customField1,
                    customField2: customField2 || transaction.recipient.customField2,
                    customField3: customField3 || transaction.recipient.customField3,
                    customField4: customField4 || transaction.recipient.customField4,
                    customField5: customField5 || transaction.recipient.customField5,
                    conversionConfigurationId:
                        conversionConfigurationId || transaction.recipient.conversionConfigurationId,
                    prevalidated,
                },
            });
        } else {
            const createRecipientResult = await this.graphQlClient().mutate<{ recipientCreate: { id: string } }>({
                mutation: createRecipientQuery,
                errorPolicy: "all",
                fetchPolicy: "no-cache",
                variables: {
                    firstName,
                    lastName,
                    address,
                    suburb,
                    state,
                    email,
                    postalCode,
                    country,
                    mobile,
                    company,
                    apartment,
                    customField1,
                    customField2,
                    customField3,
                    customField4,
                    customField5,
                    conversionConfigurationId,
                },
            });
            transaction.recipient = {
                id: createRecipientResult?.data?.recipientCreate?.id,
                firstName,
                lastName,
                address,
                suburb,
                state,
                email,
                postalCode,
                country,
                mobile,
                company,
                apartment,
                customField1,
                customField2,
                customField3,
                customField4,
                customField5,
                conversionConfigurationId,
            };
            this.experience.callEvent(WorkflowExperienceEventType.RecipientChanged);
            await this.graphQlClient().mutate({
                mutation: transactionAttachRecipientQuery,
                errorPolicy: "all",
                fetchPolicy: "no-cache",
                variables: {
                    id: this.experience.getTransaction().id,
                    recipientId: createRecipientResult.data?.recipientCreate?.id,
                },
                context: {
                    transactionOwnerId: this.experience.getTransaction().transactionOwnerId,
                },
            });
        }
        await this.fetchConversionConfiguration();
        this.recipientCallbacks.forEach((c) => c(this.experience.getTransaction().recipient));
    }

    private static configurationPromiseMap = new Map<string, Promise<ConversionConfiguration | undefined>>();

    private async fetchConversionConfiguration() {
        const recipient = this.experience.getTransaction().recipient;
        if (recipient?.conversionConfigurationId && !this.recipientConversionConfiguration) {
            const existingPromise = InternalWorkflowManager.configurationPromiseMap.get(
                recipient.conversionConfigurationId,
            );
            if (existingPromise) {
                this.recipientConversionConfiguration = await existingPromise;
            } else {
                const getConfig = async () => {
                    const response = await this.graphQlClient().query<{
                        conversionConfiguration?: ConversionConfiguration;
                    }>({
                        query: getConversionConfigurationQuery,
                        errorPolicy: "all",
                        fetchPolicy: "no-cache",
                        variables: {
                            id: recipient.conversionConfigurationId,
                        },
                    });
                    return response.data.conversionConfiguration;
                };
                const promise = getConfig();
                InternalWorkflowManager.configurationPromiseMap.set(recipient.conversionConfigurationId, promise);
                this.recipientConversionConfiguration = await promise;
            }
        }
    }

    private async initializeDefaultWorkflowState(workflow: Workflow) {
        const fireReloadAnimation = () => {
            const finalConfig = workflow.finalizeStepConfig;
            if (!finalConfig) return;
            const lookAt = finalConfig.lookAtAnimation;
            const modelAnim = finalConfig.modelAnimation;
            if (lookAt) this.previewService?.executeCameraAnimation(lookAt);
            if (modelAnim) this.modelContainer?.executeAnimation(modelAnim);
        };

        // If a reloaded state exists, we want to load the state from that instead of initializing with defaults.
        if (this.reloadedState) {
            this.reloadedState.serializableWorkflow.steps.forEach((step) => {
                this.storage[step.stepName] = step.storage || {};
            });
        }

        await this.fetchConversionConfiguration();

        // We set the scenes the workflow should start with
        this.allScenes = await getAllScenes(workflow);

        // Initialize any relevant starting silent steps.
        const activeScenes = getActiveScenes(this.allScenes, this.stepSelections);
        const introducedSilentSteps = activeScenes.map((scene) => scene.silentSteps).flat();
        const { stepElements: silentStepElements, commands: silentCommands } =
            await this.stepElementsForIntroducedSilentSteps(introducedSilentSteps, !!this.reloadedState);
        this.commandContext.apply(new GroupCommand(silentCommands), true);
        this.stepElements = { ...this.stepElements, ...silentStepElements };

        // Begin by setting the initial state of the workflow.
        await this.ensureStepsAreLoaded();

        // Sync the initial state
        if (!this.isReloadedTransaction) {
            this.updateStateWithServer();
        }

        // Initial render of the 3D scene.
        this.renderLayouts();

        // If we're dealing with a reloaded state, we want to fire of the initial animations at this point.
        this.reloadedState && fireReloadAnimation();
    }

    getWorkflowExperience() {
        return this.experience;
    }

    getClient() {
        return this.client;
    }

    setWorkflowStateSyncEnabled(enabled: boolean) {
        this.workflowStatePromiseQueue.enabled = enabled;
    }

    getInitializationPromise() {
        return this.initializationPromise;
    }

    isInitialized() {
        return this.initialized;
    }

    addConfirmCallback(callback: ConfirmCallback) {
        callback(this.confirmedDesign);
        this.confirmCallbacks.push(callback);
    }

    addEditedCallback(callback: EditedCallback) {
        callback(this.editedSteps);
        this.editedCallbacks.push(callback);
    }

    addElementsCallback(callback: ElementsCallback) {
        callback(this.stepElements);
        this.elementsCallbacks.push(callback);
    }

    addInformationResultCallback(callback: InformationResultCallback) {
        callback(this.informationResults);
        this.informationResultCallbacks.push(callback);
    }

    addInitCallback(callback: InitCallback) {
        callback(this.stepInitialised);
        this.initCallbacks.push(callback);
    }

    addMandatoryCallback(callback: MandatoryCallback) {
        callback(this.mandatorySteps);
        this.mandatoryCallbacks.push(callback);
    }

    addMetadataCallback(callback: MetadataCallback) {
        callback(this.stepMetadata);
        this.metadataCallbacks.push(callback);
    }

    async addSelectionCallback(callback: SelectionCallback) {
        callback({
            selectionCost: this.selectionCost,
            selections: this.stepSelections,
            traversableScenes: await this.traversableScenes(),
        });
        this.selectionCallbacks.push(callback);
    }

    addStepSpecificStorageCallback(callback: StepSpecificStorageCallback, stepName: string) {
        if (!Object.keys(this.storage).includes(stepName)) {
            this.storage[stepName] = {};
        }
        callback(this.storage[stepName]);
        if (!Object.keys(this.stepSpecificStorageCallbacks).includes(stepName)) {
            this.stepSpecificStorageCallbacks[stepName] = [];
        }
        this.stepSpecificStorageCallbacks[stepName].push(callback);
    }

    /**
     * @deprecated Any call to this causes all renderable steps to re-render even if they don't care about change.
     */
    addStorageCallback(callback: StorageCallback) {
        callback(this.storage);
        this.storageCallbacks.push(callback);
    }

    addMakingAdjustmentsCallback(callback: MakingAdjustmentsCallback) {
        callback(this.currentAdjustingStepId);
        this.makingAdjustmentsCallback.push(callback);
    }

    addPoller(poller: Poller) {
        this.pollers.push(poller);
    }

    addValidationCallback(callback: ValidationCallback) {
        this.validationCallbacks.push(callback);
        callback(this.validationErrors);
    }

    addRecipientCallback(callback: RecipientCallback) {
        this.recipientCallbacks.push(callback);
        callback(this.experience.getTransaction().recipient);
    }

    removeRecipientCallback(callback: RecipientCallback) {
        this.recipientCallbacks = this.recipientCallbacks.filter((it) => it !== callback);
    }

    getLayouts(): ILayout[] {
        return this.layouts;
    }

    getAllLayoutData(): LayoutState[] {
        return this.commandContext.getAllLayouts().map((l) => l.layoutState);
    }

    getLayoutPreviewService(): LayoutPreviewService | undefined {
        return this.renderableContextService;
    }

    getInformationResults(): InformationResult[] {
        return this.informationResults;
    }

    getPreviewService() {
        return this.previewService;
    }

    getCommandContext(): CommandContext {
        return this.commandContext;
    }

    getRegionElements(stepName: string): RegionElement[] {
        return this.stepElements[stepName] || [];
    }

    getStepSpecificServices(stepName: string): StepSpecificServices | undefined {
        return this.stepSpecificServices[stepName];
    }

    getTransactionCustomer(): Customer | undefined {
        return this.ownerCustomer;
    }

    setTransactionCustomer(customer: Customer) {
        this.ownerCustomer = customer;
    }

    setTransactionCustomerDetails(details: { email: string }) {
        this.ownerCustomer = { ...this.ownerCustomer, emailAddress: details.email };
    }

    getModelContainer(): ModelContainer | undefined {
        return this.modelContainer;
    }

    setModelContainer(container: ModelContainer): void {
        if (!container || (this.previewService && this.previewService.getAllModels().includes(container))) {
            this.modelContainer = container;
            if (container && container.metadata && container.metadata instanceof Map) {
                this.modelContainer.metadata.set("workflowManager", this);
            }
        }
    }

    getCommandDispatcher() {
        return (command: CanvasCommand) => this.commandContext.apply(command, !this.initialized);
    }

    getWorkflowSelections(): WorkflowSelections {
        const selections: WorkflowSelections = {};
        this.experience.getWorkflow()?.steps.forEach((step) => {
            const stepStorage = this.stepSelections[step.stepName];
            if (stepStorage && stepStorage.selectedVariants && stepStorage.selectedVariants.length > 0) {
                selections[step.stepName] = {
                    selections: stepStorage.selectedVariants,
                };
            }
        });
        return selections;
    }

    getStepSelections(): StepSelections {
        return this.stepSelections;
    }

    markStepsAsInitialised(stepNames: string[]) {
        const stepInitialised = { ...this.stepInitialised };
        stepNames.forEach((stepName) => {
            stepInitialised[stepName] = true;
        });
        this.stepInitialised = stepInitialised;
        this.onInitChange();
    }

    getUpdatesPending() {
        return this.pendingUpdates.length > 0;
    }

    markUpdateCompleted(id: string) {
        this.pendingUpdates = this.pendingUpdates.filter((up) => up !== id);
    }

    markUpdatePending() {
        const updateId = generate();
        this.pendingUpdates = [...this.pendingUpdates, updateId];
        return updateId;
    }

    setEditedStatus(stepName: string, status: boolean) {
        this.editedSteps = { ...this.editedSteps, [stepName]: status };
        this.onEditedChange();
    }

    setInformationResults(results: InformationResult[]) {
        this.informationResults = [...results];
        this.onInformationResultChange();
    }

    setCurrentAdjustingStepId(stepId: string) {
        this.currentAdjustingStepId = stepId;
        this.onMakingAdjustmentsChange();
    }

    setMandatoryFulfilled(stepName: string, status: boolean) {
        // If the value hasn't changed don't notify listeners.
        if (this.mandatorySteps[stepName] === status) {
            return;
        }
        // Otherwise update mandatory structure and notify
        this.mandatorySteps = { ...this.mandatorySteps, [stepName]: status };
        this.onMandatoryChange();
    }

    getMandatoryUnfulfilledSteps(): string[] {
        // Get the active mandatory steps.
        const scenes = getActiveScenes(this.allScenes, this.stepSelections);
        const steps = scenes.flatMap((scene) => scene.renderableSteps);
        const mandatorySteps = steps.filter((s) => s.mandatory);

        // A mandatory step is unfulfilled if its value is false or absent.
        const unfulfilledSteps = mandatorySteps.filter((s) => !this.mandatorySteps[s.stepName]);
        return unfulfilledSteps.map((s) => s.stepName);
    }

    setStepError(stepName: string, field: string, error: string | undefined) {
        let changed = false;
        if (!this.validationErrors.steps.has(stepName) && error) {
            // New step entry
            changed = true;
            this.validationErrors.steps.set(stepName, new Map([[field, error]]));
        } else if (this.validationErrors.steps.has(stepName)) {
            const stepErrors = this.validationErrors.steps.get(stepName)!;
            if (error && error !== stepErrors.get(field)) {
                // New or changed
                changed = true;
                stepErrors.set(field, error);
            } else if (!error) {
                // Deleted entry
                changed = true;
                stepErrors.delete(field);
                if (stepErrors.size === 0) {
                    // Delete step if no errors
                    this.validationErrors.steps.delete(stepName);
                }
            }
        }

        if (changed) {
            this.onValidationChange();
        }
    }

    getStepErrors(stepName: string) {
        return this.validationErrors.steps.get(stepName);
    }

    getValidationErrors() {
        return this.validationErrors;
    }

    toggleDesignConfirmed() {
        this.confirmedDesign = !this.confirmedDesign;
        this.onConfirmChange();
    }

    toggleMakingAdjustmentsCallback(callback: MandatoryCallback) {
        callback(this.mandatorySteps);
        this.mandatoryCallbacks.push(callback);
    }

    async injectIntoPreviewService(previewService: ThreeDPreviewService, refocusCamera?: boolean): Promise<void> {
        if (this.modelContainer) {
            throw new Error("You must eject from the preview service before injecting again.");
        }
        this.previewService = previewService;
        const product = this.experience.getProduct();
        if (!product?.modelUrl || !this.previewService) return;
        const container = previewService.loadModel(
            {
                model: product.modelUrl,
                contextService: this.getLayoutPreviewService(),
            },
            { refocusCamera },
        );
        this.setModelContainer(container);
        const scenes = getActiveScenes(this.allScenes, this.stepSelections).filter((scene) => scene);
        const steps = scenes
            .map((scene) => scene.renderableSteps)
            .flat()
            .filter((step) => step.type === StepType.Model || step.type === StepType.Material);
        steps.forEach((step) => {
            if (!step.option?.id) {
                console.error(`Failed to read option id from step: ${step.stepName}`);
                return;
            }
            const selectedVariants = this.stepSelections[step.stepName]?.selectedVariants || [];
            if (selectedVariants.length === 0) return;
            const variant = selectedVariants[0];
            if (step.type === StepType.Model) {
                if (!this.modelContainer)
                    throw new UnhandledBehaviorError(
                        "We should always have a model container when injecting a preview!",
                    );
                const modelRef = variant.asset?.fileLink;
                if (!modelRef) {
                    console.error(`Failed to read model url from variant: ${variant.id} for step: ${step.stepName}`);
                    return;
                }
                const data = step.data as ModelStepData;
                this.modelContainer.applyModelVariant(
                    step.stepName,
                    {
                        model: modelRef,
                        contextService: this.getLayoutPreviewService(),
                    },
                    data.replaceProductModel || false,
                );
            } else {
                const material = variant.material;
                if (!material) {
                    console.error(`Failed to read material from variant: ${variant.id} for step: ${step.stepName}`);
                    return;
                }
                const data = step.data as MaterialStepData;
                data.targetMaterials.forEach((matName) => {
                    if (!this.modelContainer)
                        throw new UnhandledBehaviorError(
                            "We should always have a model container when injecting a preview!",
                        );
                    this.modelContainer.applyMaterialVariant(matName, step.option!.id!, material);
                });
            }
        });
        await container.getInitializationPromise();
        this.renderLayouts();
    }

    ejectFromPreviewService(): void {
        if (this.modelContainer) {
            if (!this.modelContainer.dispose) {
                throw new Error("This feature is not supported in the current preview service.");
            }
            this.modelContainer.dispose();
            this.modelContainer = undefined;
        }
        this.previewService = undefined;
    }

    updateStorage(stepName: string, update: StepStorage) {
        const newStorage = {
            ...this.storage,
            [stepName]: { ...this.storage[stepName], ...update },
        };
        if (!isEqual(newStorage, this.storage)) {
            this.storage = newStorage;
            const workflowCommand = new UpdateWorkflowStateCommand(this.constructSerializableWorkflow());
            this.commandContext.apply(workflowCommand, true);
            this.onStepSpecificStorageChange(stepName);
            this.onStorageChange();
        }
    }

    private constructSerializableWorkflow(): SerializableWorkflow {
        const steps: SerializableStep[] = [];
        this.allScenes.forEach((scene) => {
            scene.renderableSteps.forEach((step) => {
                const stepStructure: SerializableStep = { stepName: step.stepName };
                stepStructure.storage = this.storage[step.stepName];
                stepStructure.selectedVariants = this.stepSelections[step.stepName]?.selectedVariants?.map(
                    (variant) => {
                        return { id: variant.id!, priceModifier: variant.priceModifier };
                    },
                );
                steps.push(stepStructure);
            });
        });
        return { steps };
    }

    private updateStateWithServer() {
        if (this.readOnly) return;
        const workflowState = this.serialize();
        this.workflowStatePromiseQueue.enqueue(
            new WorkflowStatePromise(async () => {
                // HACK: Immediately firing a network request can block the thread when many fire at once.
                // Using setTimeout will push this promise to the end of the event loop, allowing the calling function to exit.
                await new Promise<void>((resolve) => {
                    setTimeout(() => {
                        resolve();
                    }, 1);
                });
                await this.updateTransaction({
                    variables: {
                        id: this.experience.getTransaction().id,
                        workflowState,
                    },
                });
            }),
        );
    }

    private renderLayouts = () => {
        if (!this.previewService) return;
        const layouts = this.getCommandContext().getAllLayouts();
        const previewHandles = this.getLayoutPreviewService()?.getAll();
        if (!previewHandles) return;
        for (const [, previewBridge] of previewHandles) {
            previewBridge.render(layouts);
        }
    };

    public async updateStateWithServerImmediate() {
        if (this.readOnly) return;
        const workflowState = this.serialize();
        return new WorkflowStatePromise(async () => {
            await this.updateTransaction({
                variables: {
                    id: this.experience.getTransaction().id,
                    workflowState,
                },
            });
        }).execute();
    }

    // Define the replacer function outside the method, or as a helper, for clarity
    private sortKeysReplacer = (key: string, value: any): any => {
        // Check if the value is a non-null object that is not an Array
        if (value && typeof value === "object" && !Array.isArray(value)) {
            // Create a new object with keys sorted alphabetically
            return Object.keys(value)
                .sort()
                .reduce((sorted, key) => {
                    // Recursively process the value to ensure nested objects are also sorted
                    sorted[key] = value[key];
                    return sorted;
                }, {} as any);
        }
        // Return primitives and arrays as is
        return value;
    };

    private serialize(): string {
        const fullState = this.getCommandContext().getState();
        if (!fullState) {
            throw new UnhandledBehaviorError("Attempted to serialize state before it was initialized.");
        }
        const deepClone = cloneDeep(fullState.transaction);
        const dehydrated = this.dehydrateState(deepClone);
        return JSON.stringify(dehydrated, this.sortKeysReplacer, undefined);
    }

    async getStateHash(): Promise<string> {
        const state = this.getCommandContext().getState();
        const modificationIds = Object.values(state?.transaction.layouts || {}).map((l) => l.modificationID ?? "");
        const alphabetizedModificationIds = modificationIds.sort().join("");

        const encoder = new TextEncoder();
        const data = encoder.encode(alphabetizedModificationIds);

        // Browser Web Crypto API (preferred)
        if (
            typeof window !== "undefined" &&
            window.crypto &&
            (window.crypto as any).subtle &&
            (window.crypto as any).subtle.digest
        ) {
            const hashBuffer = await (window.crypto as any).subtle.digest("SHA-256", data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
            return hash;
        }

        // Node fallback using dynamic import of 'crypto'
        try {
            const crypto = await import("crypto");
            return crypto.createHash("sha256").update(alphabetizedModificationIds, "utf8").digest("hex");
        } catch (e) {
            throw new Error("Unable to compute SHA-256 hash: no suitable crypto implementation available.");
        }
    }

    /**
     * A helper to remove unwanted local cache data from the state before sending to the server.
     */
    private dehydrateState(state: LayoutsState) {
        for (const el of Object.values(state.layouts)
            .map((lay) => lay.elements)
            .flat()) {
            // FIXME:" Can we achieve this better? Maybe non serializable fields could be removed entirely from the element."
            if (el.type === "illustration") {
                delete (el as IllustrationElement).cachedObjectURL;
                // When a src is provided for an svg let's remove the parsed data from the network request.
                if ((el as IllustrationElement).src) delete (el as IllustrationElement).svg;
            }
            if (el.type === "frame") {
                delete (el as FrameElement).pattern?.svg;
            }
        }
        // Ensure the __typename field used by apollo is not sent to the server.
        this.deleteFieldNameFromObjectRecursive(state, "__typename");
        return state;
    }

    private deleteFieldNameFromObjectRecursive(obj: any, fieldName: string) {
        if (typeof obj !== "object" || obj === null) {
            return;
        }
        for (const key in obj) {
            if (key === fieldName) {
                delete obj[key];
            } else {
                this.deleteFieldNameFromObjectRecursive(obj[key], fieldName);
            }
        }
    }

    async outstandingRequestsPromise() {
        return this.workflowStatePromiseQueue.finalize();
    }

    updateMetadata(stepName: string, update: any) {
        this.stepMetadata = {
            ...this.stepMetadata,
            [stepName]: { ...this.stepMetadata[stepName], ...update },
        };
        this.onMetadataChange();
    }

    private removeElements(elements: RegionElement[]) {
        const filterOutAllElementsFromSteps = (
            existingStepElements: StepElements,
            elements: RegionElement[],
        ): StepElements => {
            return Object.keys(existingStepElements).reduce<StepElements>((stepsWithoutOldElements, stepName) => {
                const newListOfStepElements = [...(existingStepElements[stepName] || [])];
                elements.forEach((regionElementToRemove) => {
                    const index = newListOfStepElements.findIndex((currentRegionElement) => {
                        return currentRegionElement.id === regionElementToRemove.id;
                    });
                    if (index > -1) {
                        newListOfStepElements.splice(index, 1);
                    }
                });
                stepsWithoutOldElements[stepName] = newListOfStepElements;
                return stepsWithoutOldElements;
            }, {});
        };
        this.stepElements = filterOutAllElementsFromSteps(this.stepElements, elements);
    }

    private getInvalidCanvasRegions(): RegionElement[] {
        return (
            this.experience.getWorkflow()?.steps.reduce<RegionElement[]>((selectionsToRemove, step) => {
                if (!stepConditionsAreSatisfied(step, this.stepSelections)) {
                    if (this.stepElements[step.stepName]) {
                        this.stepElements[step.stepName].forEach((regionElement: RegionElement) =>
                            selectionsToRemove.push(regionElement),
                        );
                    }
                }
                return selectionsToRemove;
            }, []) || []
        );
    }

    private getInvalidModelVariants(): string[] {
        return (
            this.experience.getWorkflow()?.steps.reduce<string[]>((invalidVariants, step) => {
                if (step.type === StepType.Model && !stepConditionsAreSatisfied(step, this.stepSelections)) {
                    invalidVariants.push(step.stepName);
                }
                return invalidVariants;
            }, []) || []
        );
    }

    private async stepElementsForIntroducedSilentSteps(
        introducedSilentSteps: Step<AnyStepData>[],
        handlingReload: boolean,
    ): Promise<{ stepElements: StepElements; commands: CanvasCommand[] }> {
        const product = this.experience.getProduct();
        if (!product) {
            return Promise.resolve({ stepElements: {}, commands: [] });
        }

        // Trigger the given silent step.
        // Steps in completely silent scenes should trigger immediately when the step becomes relevant.
        // Steps in renderable scenes should trigger when the scene becomes active the first time.
        const executeSilentStep = async (
            step: Step<AnyStepData>,
            layouts: ILayout[],
            product: Product,
        ): Promise<{ step: Step<AnyStepData>; results: SilentStepTriggerResult[] }> => {
            if (step.type === StepType.SilentIllustration) {
                const result = await new SilentStepHandler(step as Step<SilentIllustrationStepData>, layouts).trigger();
                return { step, results: result };
            } else if (step.type === StepType.ProductOverlay) {
                const result = await new SilentStepHandler(
                    step as Step<ProductOverlayStepData>,
                    layouts,
                    product,
                ).trigger();
                return { step, results: result };
            }

            return Promise.reject("Unknown silent step. This is a bug");
        };

        const uninitializedSteps: Step<AnyStepData>[] = introducedSilentSteps.filter(
            (step) => !this.stepInitialised[step.stepName],
        );

        const result: { stepElements: StepElements; commands: CanvasCommand[] } = {
            stepElements: {},
            commands: [],
        };

        const silentStepPromises: Promise<{ step: Step<AnyStepData>; results: SilentStepTriggerResult[] }>[] = [];

        for (const step of uninitializedSteps) {
            this.markStepsAsInitialised([step.stepName]);
            if (!handlingReload) {
                silentStepPromises.push(executeSilentStep(step, this.layouts, product));
            }
        }
        const silentStepResults = await Promise.all(silentStepPromises);
        for (const stepResult of silentStepResults) {
            result.stepElements[stepResult.step.stepName] = stepResult.results.map((r) => r.regionElement);
            result.commands = [...result.commands, ...stepResult.results.map((r) => r.command)];
        }

        return result;
    }

    getStepStorage(stepName: string): StepStorage | undefined {
        return this.storage[stepName];
    }

    getMetadata(stepName: string): StepMetadata | undefined {
        return this.stepMetadata[stepName];
    }

    getWorkflowMetadata(): WorkflowMetadata {
        return this.stepMetadata;
    }

    getStepTags(stepId: string): string[] {
        return this.stepTags[stepId] || [];
    }

    /**
     * @deprecated Simply run serializedSteps.find((step) => step.stepName === stepName) instead.
     */
    getSerializedStep(stepName: string, serializedSteps: SerializableStep[]): SerializableStep | undefined {
        return serializedSteps.find((step) => step.stepName === stepName);
    }

    async updateTransactionShareActions() {
        const shareActions = await this.client.getShareActionsForTransaction(this.experience.getTransaction().id);
        this.experience.getTransaction().transactionShareActions = shareActions;
    }

    async updateTransactionStakeholders() {
        const response = await graphQlManager.getShadowGraphqlClient().query<{ transactions: Transaction[] }>({
            query: getTransactionStakeholdersQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                id: this.experience.getTransaction().id,
            },
        });
        if (!response?.data?.transactions || response.data.transactions.length !== 1) {
            this.experience.getTransaction().currentStakeholder = undefined;
            this.experience.getTransaction().stakeholders = undefined;
        } else {
            this.experience.getTransaction().stakeholders = response.data.transactions[0].stakeholders;
            this.experience.getTransaction().currentStakeholder = response.data.transactions[0].currentStakeholder;
        }
    }

    async approveTransaction(note?: string): Promise<void> {
        await graphQlManager.getShadowGraphqlClient().mutate<{ id: string }>({
            mutation: transactionApprovalQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                id: this.experience.getTransaction().id,
                note,
            },
        });
    }

    async rejectTransaction(note?: string): Promise<void> {
        await graphQlManager.getShadowGraphqlClient().mutate<{ id: string }>({
            mutation: transactionRejectionQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                id: this.experience.getTransaction().id,
                note,
            },
        });
    }

    async reset() {
        this.stepElements = {};
        this.stepInitialised = {};
        this.stepMetadata = {};
        this.stepSelections = {};
        this.storage = {};
        await this.ensureStepsAreLoaded();
    }

    async setSelectionsAndElements(
        stepName: string,
        variants: VariantResource[],
        elements: RegionElement[],
        callback?: () => Promise<void>,
    ) {
        const workflow = this.experience.getWorkflow();
        if (!workflow) throw new Error("Attempted to set selections when no workflow is set!");

        const oldSelections = this.stepSelections;
        this.stepSelections = {
            ...this.stepSelections,
            [stepName]: { selectedVariants: variants },
        };

        // Recursively tally the cost of all selections
        this.selectionCost = Object.values(this.stepSelections).reduce((total, current) => {
            return (
                total +
                current
                    .selectedVariants!.map((vs) => vs.priceModifier || 0)
                    .reduce((acc, val) => {
                        return acc + val;
                    }, 0)
            );
        }, 0);

        // Unset state of steps which have become irrelevant.
        workflow.steps.forEach((step) => {
            if (!stepConditionsAreSatisfied(step, this.stepSelections)) {
                this.stepInitialised[step.stepName] = false;
                delete this.stepMetadata[step.stepName];
                delete this.stepSelections[step.stepName];
                delete this.storage[step.stepName];
            }
        });

        // Find silent steps which have become relevant, cleanup any that have become irrelevant.
        const allScenes = this.allScenes;
        const oldScenes = getActiveScenes(allScenes, oldSelections);
        const newScenes = getActiveScenes(allScenes, this.stepSelections);
        const oldSilentSteps = oldScenes.map((scene) => scene.silentSteps).flat();
        const newSilentSteps = newScenes.map((scene) => scene.silentSteps).flat();
        const introducedSilentSteps = newSilentSteps.filter(
            (newStep) => !oldSilentSteps.some((oldStep) => oldStep.stepName === newStep.stepName),
        );
        oldSilentSteps.forEach((step) => {
            if (!stepConditionsAreSatisfied(step, this.stepSelections)) {
                this.stepInitialised[step.stepName] = false;
            }
        });

        const invalidCanvasRegions = this.getInvalidCanvasRegions();

        const { stepElements: silentStepElements, commands: silentCommands } =
            await this.stepElementsForIntroducedSilentSteps(introducedSilentSteps, false);

        // Set the new step elements
        this.stepElements = {
            ...this.stepElements,
            ...silentStepElements,
            [stepName]: elements,
        };

        // Remove any elements that have become invalid.
        this.removeElements(invalidCanvasRegions);

        // When target step is a frame, we need to notify frame service of new elements.
        const step = workflow.steps.find((step) => step.stepName === stepName);
        if (step?.type === StepType.Frame) {
            const frameService = (
                this.getWorkflowExperience().getStepById(step.stepName) as undefined | FrameStepHandle
            )?.frameService;
            frameService?.setTargetElements(elements.map((regionEl) => regionEl.id));
        }

        const deleteCommands = invalidCanvasRegions.map((el: RegionElement) => new DeleteElementCommand(el.id));
        const workflowCommand = new UpdateWorkflowStateCommand(this.constructSerializableWorkflow());
        const commands = [...silentCommands, ...deleteCommands, workflowCommand];
        if (commands.length > 0) {
            this.commandContext.apply(new GroupCommand(commands), true);
        }

        // Ensure any effects of this selection change are executed as a result.
        await this.ensureStepsAreLoaded();

        // Notify any listeners that the workflow has changed.
        this.onElementsChange();

        // When conditions change certain models may become invalid. We clean them up here.
        const invalidModelVariants = this.getInvalidModelVariants();
        const modelContainer = this.modelContainer;
        if (modelContainer) {
            const removalPromises = invalidModelVariants.map((im) =>
                modelContainer.applyModelVariant(
                    im,
                    {
                        contextService: this.getLayoutPreviewService(),
                    },
                    false,
                ),
            );
            await Promise.all(removalPromises);
        }

        await this.onSelectionChange();

        if (callback) {
            await callback();
        }
    }

    private async ensureStepsAreLoaded() {
        const activeScenes = getActiveScenes(this.allScenes, this.stepSelections);
        const stepsPerScene = activeScenes.map((scene) => scene.renderableSteps);

        // We create a list of steps which are not yet initialized.
        // Iterate over all required steps and run initialization, this should occur in parallel.
        const stepPromises: Promise<any>[] = [];
        const steps = stepsPerScene.flat();

        for (const step of steps) {
            if (!this.stepInitialised[step.stepName]) {
                this.stepInitialised[step.stepName] = true;
                switch (step.type) {
                    case StepType.DigitalContent:
                        stepPromises.push(
                            digitalContentStepService.init(
                                step as Step<DigitalContentStepData>,
                                this,
                                this.reloadedState,
                            ),
                        );
                        break;
                    case StepType.Frame:
                        stepPromises.push(frameStepService.init(step as Step<FrameStepData>, this, this.reloadedState));
                        break;
                    case StepType.Illustration:
                        stepPromises.push(
                            illustrationStepService.init(step as Step<IllustrationStepData>, this, this.reloadedState),
                        );
                        break;
                    case StepType.Material:
                        stepPromises.push(
                            materialStepService.init(step as Step<MaterialStepData>, this, this.reloadedState),
                        );
                        break;
                    case StepType.Model:
                        stepPromises.push(modelStepService.init(step as Step<ModelStepData>, this, this.reloadedState));
                        break;
                    case StepType.Module:
                        // TODO: Mimic frame handles and scrap step specific services
                        this.stepSpecificServices[step.stepName] = {
                            module: await moduleResolver((step as Step<ModuleStepData>).data.module),
                        };
                        stepPromises.push(
                            moduleStepService.init(step as Step<ModuleStepData>, this, this.reloadedState),
                        );
                        break;
                    case StepType.Picture:
                        stepPromises.push(
                            pictureStepService.init(step as Step<PictureStepData>, this, this.reloadedState),
                        );
                        break;
                    case StepType.Question:
                        stepPromises.push(
                            questionStepService.init(step as Step<QuestionStepData>, this, this.reloadedState),
                        );
                        break;
                    case StepType.Shape:
                        stepPromises.push(shapeStepService.init(step as Step<ShapeStepData>, this, this.reloadedState));
                        break;
                    case StepType.Text:
                        stepPromises.push(textStepService.init(step as Step<TextStepData>, this, this.reloadedState));
                        break;
                    default:
                        break;
                }
            }
        }

        // Settle all parallel logic.
        const stepCwfs = (await Promise.allSettled<CommandWithFollowup | null>(stepPromises)).map((result) => {
            if (result.status === "rejected") {
                throw new Error(`Step initialization failed: ${result.reason}`);
            }
            return result.value;
        });

        // Map all non-null commands/followups to lists.
        const commands = stepCwfs.filter((cwf) => !!cwf && !!cwf.command).map((cwf) => cwf!.command!);
        const followups = stepCwfs.filter((cwf) => !!cwf && !!cwf.followup).map((cwf) => cwf!.followup!);

        // Execute all commands in a single batch
        if (commands && commands.length > 0) this.commandContext.apply(new GroupCommand(commands), true);
        for (const followup of followups) await followup();

        // Process any followup conditions
        // A step will produce followup actions when a change has occured. To ensure
        // any conditions that have become true are actioned, we run once again.
        if (followups.length > 0) {
            await this.ensureStepsAreLoaded();
        }

        // Notify listeners of changes.
        this.onInitChange();
    }

    private onConfirmChange() {
        this.confirmCallbacks.forEach((callback) => callback(this.confirmedDesign));
    }

    private onEditedChange() {
        this.editedCallbacks.forEach((callback) => callback(this.editedSteps));
    }

    private onElementsChange() {
        this.elementsCallbacks.forEach((callback) => callback(this.stepElements));
    }

    private onInformationResultChange() {
        this.informationResultCallbacks.forEach((callback) => callback(this.informationResults));
    }

    private onInitChange() {
        this.initCallbacks.forEach((callback) => callback(this.stepInitialised));
    }

    private onMakingAdjustmentsChange() {
        this.makingAdjustmentsCallback.forEach((callback) => {
            callback(this.currentAdjustingStepId);
        });
    }

    private onMandatoryChange() {
        this.mandatoryCallbacks.forEach((callback) => callback(this.mandatorySteps));
    }

    private onMetadataChange() {
        this.metadataCallbacks.forEach((callback) => {
            callback(this.stepMetadata);
        });
    }

    private async onSelectionChange() {
        const traversableScenes = await this.traversableScenes();
        for (const cb of this.selectionCallbacks) {
            cb({
                selectionCost: this.selectionCost,
                selections: this.stepSelections,
                traversableScenes,
            });
        }
    }

    private onStepSpecificStorageChange(stepName: string) {
        if (!Object.keys(this.stepSpecificStorageCallbacks).includes(stepName)) {
            this.stepSpecificStorageCallbacks[stepName] = [];
        }
        this.stepSpecificStorageCallbacks[stepName].forEach((callback) => callback(this.storage[stepName]));
    }

    private onStorageChange() {
        this.storageCallbacks.forEach((callback) => callback(this.storage));
    }

    private onValidationChange() {
        this.validationCallbacks.forEach((callback) => callback(this.validationErrors));
    }

    traversableScenes(): Promise<WorkflowScene[]> {
        return getRenderableRelevantScenes(this.allScenes, this.stepSelections, this.singleVariantsRenderable);
    }
}
